import {
  AmbientLight,
  Box3,
  Cache,
  PerspectiveCamera,
  Scene,
  Vector3,
  Vector2,
  WebGLRenderer,
  Object3D,
  Mesh,
  Color,
  DoubleSide,
  ACESFilmicToneMapping,
  PlaneGeometry,
  RepeatWrapping,
  SRGBColorSpace,
  Raycaster,
  OrthographicCamera,
  MeshStandardMaterial,
  FogExp2,
  BasicShadowMap,
  PCFShadowMap,
  TextureLoader,
  LinearSRGBColorSpace,
  Box3Helper,
  Float32BufferAttribute
} from "three";
//import * as THREE from "three"; // 从r140版本引入sRGBEncoding等一些常量需要用 THREE.sRGBEncoding，无法使用import方式
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { TransformControls } from "../lib/TransformControls.js"; //"three/examples/jsm/controls/TransformControls.js";
import { CustomControls } from "../lib/CustomControls.js";
import Util from "../utils/utils.js";
import { Constant } from "../utils/constant.js";
import { BaseManager } from "./BaseManager.js";
import { EventManagerCommand } from "./EventManager";
import { createBackground } from "../lib/three-vignette.js";

import { CSG } from "three-csg-ts";
import { ProjectionTool } from "../utils/ProjectionTool.js";

import { ClickMode } from "../Interface3D.js";
import { OutlineControl } from "../utils/OutlineContorl.js";
import { TransformCommand } from "../command/transformCommand.js";

import { Water } from "three/examples/jsm/objects/Water.js";
import { terrainMaterial } from "../utils/Terrain.js";
import { platMaterial } from "../utils/plat.js";

import  Stats  from "three/examples/jsm/libs/stats.module.js";
import { Vector } from "three-csg-ts/lib/esm/Vector.js";

const TWEEN = require("@tweenjs/tween.js").default;

const IS_IOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

Cache.enabled = true;

export class SceneManager extends BaseManager {
  constructor(manager) {
    super(manager);
  }

  async init(manager) {
    super.init(manager);
    this.el = manager.el;
    this.options = manager.options;

    // ============================迁移的变量

    this.totalModelCount = 0;
    this.loadedModelCount = 0;

    this.state = {
      aspect: false, //高度占比
      dispose: false,
      loadratioStart: 0.2, //模型加载起始比
      loadratio: 0.6, //模型加载占比
      addLights: true,
      ambientIntensity: 0.5,
      ambientColor: 0xffffff,
      bgcolor1: this.options.sceneconfigure.bgColor1 || "#ffffff", //options.sceneconfigure.bgColor1 || "#ffffff",
      bgcolor2: this.options.sceneconfigure.bgColor2 || "#353535",
      clientWidth: this.el.clientWidth,
      clientHeight: this.el.clientHeight,
    };

    this.isInit = false;
    this.singleModels = []; // 保存单体对象，如一颗树、一张桌子
    this.currentSelectObj = [];
    // ============================

    this.viewMode = "default"; // 当前视图模式，默认为透视视图，"default"/"top"/"front"/"back"/"left"/"right"

    this._initScene();

    const fov = 70;
    this.defaultCamera = new PerspectiveCamera(
      fov,
      this.state.clientWidth / this.state.clientHeight,
      0.01,
      500
    );
    this.scene.add(this.defaultCamera);
    this.defaultCamera.lookAt(new Vector3(0, 0, 0));
    this.activeCamera = this.defaultCamera;

    this.renderer = window.renderer = new WebGLRenderer({
      antialias: true,
      preserveDrawingBuffer: true,
      logarithmicDepthBuffer: true, // 开启这个可以解决z-fitting问题，但是会导致shaderMaterial渲染顺序问题（可以使用ShaderChunk解决，https://blog.csdn.net/u010657801/article/details/129500470）
      stencil: true,
    });
    this.renderer.physicallyCorrectLights = true;
    this.renderer.autoClearStencil = false; // 手动管理模板清除
    this.renderer.outputColorSpace = LinearSRGBColorSpace;
    this.renderer.setClearColor(this.state.bgcolor1, 1);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(this.state.clientWidth, this.state.clientHeight);
    this.renderer.autoClear = false;
    this.renderer.localClippingEnabled = true;
    this.renderer.toneMapping = ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 2;
    this.renderer.info.autoReset=false;
    this.renderer.sortObjects=true;

    this.renderer.debug.checkShaderErrors = true;
    this.el.appendChild(this.renderer.domElement);

    this.outlineControl = new OutlineControl(
      this.renderer,
      this.scene,
      this.activeCamera,
      this.el
    );

    //this._createVignette();

    this._initControls();

    this.isRenderContextReShown = false;

    this.isRunning = true; //控制是否执行循环函数，清理页面时设为false，不可逆

    this._setupFocusChangeEvent();

    this._addLights();

    this._addFog();

    this._createPlane();

    setTimeout(() => {
      this._addListener();
    }, 500);

    // 执行场景管理器初始化完成的事件。使用settimeout函数是要让这一步的执行异步，也就是先返回这个init函数，让manager拥有sceneManager对象，不然会报错不存在sceneManager对象
    setTimeout(() => {
      if (this.manager.eventManager) {
        this.manager.eventManager.dispach(
          EventManagerCommand.sceneManagerInitCompleted
        );
      }
    }, 0);

    this.stats=new Stats();
    this.stats.showPanel(0); // 0: fps, 1: ms, 2: mb, 3: gc, 4: custom
    this.stats.dom.style.position = "absolute";
    this.stats.dom.style.left = "0px";
    this.stats.dom.style.top = "0px";
    document.body.appendChild(this.stats.dom);
    this.stats2=new Stats();
    this.stats2.showPanel(1); // 0: fps, 1: ms, 2: mb, 3: gc, 4: custom
    this.stats2.dom.style.position = "absolute";
    this.stats2.dom.style.left = "80px";
    this.stats2.dom.style.top = "0px";
    document.body.appendChild(this.stats2.dom);
    this.stats3=new Stats();
    this.stats3.showPanel(2); // 0: fps, 1: ms, 2: mb, 3: gc, 4: custom
    this.stats3.dom.style.position = "absolute";
    this.stats3.dom.style.left = "160px";
    this.stats3.dom.style.top = "0px";
    document.body.appendChild(this.stats3.dom);

    this.projectionTool=new ProjectionTool(this.manager);

  }

  update(dt) {
    super.update(dt);
    this._animate(dt);
  }

  clear() {
    this.state.dispose = true;

    this._removeListener();
    for (var i = 0; i < this.singleModels.length; i++) {
      let content = this.singleModels[i];
      this.scene.remove(content);

      // dispose textures
      traverseMaterials(content, (material) => {
        Constant.MAP_NAMES.forEach((map) => {
          if (material[map]) material[map].dispose();
        });
      });

      // dispose geometry
      content.traverse((node) => {
        if (!node.isMesh) return;
        node.geometry.dispose();
      });
    }

    if (this.scene) Util.disposeFun(this.scene);

    if (this.customControls) this.customControls.dispose();

    if (this.outlineControl) this.outlineControl.clear();

    this.isRunning = false;

    this.materials = {};
    this.bloomArray = [];
    if (this.controls) this.controls = null;

    let gl = this.renderer.domElement.getContext("webgl");
    if (gl) gl.getExtension("WEBGL_lose_context").loseContext();

    this.el.innerHTML = "";
    this.el = null;
    this.renderer.forceContextLoss();
    this.renderer.clear();
    this.renderer.dispose();
    this.renderer.domElement = null;
    this.renderer = null;

    this.scene.clear();
    this.scene = null;
    //console.log("scene clear");

    super.clear();
  }

  _createVignette() {
    this.vignette = createBackground({
      aspect: this.defaultCamera.aspect,
      grainScale: IS_IOS ? 0 : 0.001, // mattdesl/three-vignette-background#1
      colors: [this.state.bgcolor1, this.state.bgcolor2],
    });
    this.vignette.name = "Vignette";
    this.vignette.renderOrder = -1;

    this.scene.add(this.vignette);
  }

  /**
   * 焦点不在当前页面时不渲染
   */
  _setupFocusChangeEvent() {
    var hidden, visibilityChange;
    const viewerSelf = this;
    if (typeof document.hidden !== "undefined") {
      // Opera 12.10 and Firefox 18 and later support
      hidden = "hidden";
      visibilityChange = "visibilitychange";
    } else if (typeof document.msHidden !== "undefined") {
      hidden = "msHidden";
      visibilityChange = "msvisibilitychange";
    } else if (typeof document.webkitHidden !== "undefined") {
      hidden = "webkitHidden";
      visibilityChange = "webkitvisibilitychange";
    }

    function handleVisibilityChange() {
      if (document[hidden]) {
      } else {
        viewerSelf.isRenderContextReShown = true;
      }
    }

    if (
      typeof document.addEventListener === "undefined" ||
      hidden === undefined
    ) {
      console.log("Browser doesn't support the Page Visibility API.");
    } else {
      document.addEventListener(
        visibilityChange,
        handleVisibilityChange,
        false
      );
    }
  }

  /**
   * 初始化场景
   */
  _initScene() {
    this.scene = new Scene(); //普通场景
    this.scene.name = "mainScene";

    this.transformScene = new Scene();
  }

  /**
   * 初始化控制器
   */
  _initControls() {
    if (false) {
      this.controls = new OrbitControls(
        this.activeCamera,
        this.renderer.domElement
      );
      this.controls.screenSpacePanning = false;
      //this.controls.maxDistance = 50;
      this.controls.maxPolarAngle = Util.degToRad(89.9);
      this.controls.minDistance = 0.2;
      this.controls.enablePan = true;
      this.controls.enableDamping = true;
      this.controls.dampingFactor = 0.1;
      this.controls.zoomSpeed = 1.5;
      this.controls.staticMoving = true;
    } else {
      this.customControls = new CustomControls(
        this.activeCamera,
        this.renderer.domElement
      );
      this.customControls.movementSpeed = 5;
      this.customControls.lookSpeed = 5;
    }

    this.transformcontrol = new TransformControls(
      this.activeCamera,
      this.renderer.domElement
    );

    this.transformcontrol.addEventListener("dragging-changed", (event) => {
      if (this.controls) this.controls.enabled = !event.value;
      if (this.customControls) this.customControls.enabled = !event.value;
      if (this.manager.interface && this.manager.interface.boxSelection) {
        this.manager.interface.boxSelection.changeMode(!event.value);
      }

      if (event.value) {
        this.oldState = {
          position: this.transformcontrol.object.position.clone(),
          scale: this.transformcontrol.object.scale.clone(),
          quaternion: this.transformcontrol.object.quaternion.clone(),
        };
      } else {
        const command = new TransformCommand(
          this.transformcontrol.object,
          this.oldState,
          this.manager.interface.updateUIByChangeModel.bind(
            this.manager.interface
          )
        );
        this.manager.addHistory(command);
      }
    });
    this.transformcontrol.addEventListener("objectChange", (obj) => {
      if (this.manager.interface.clickMode === ClickMode.SceneModel) {
        if (this.shiftKey && this.transformcontrol.mode === "translate") {
          // 需要检测地面吸附
          if (this.transformcontrol.objBox) {
            // 除了默认地面外还指定了其他地面
            if (this.realFloor) {
              const obj = this.transformcontrol.object;
              if (!obj) return;
              // 筛选当前选中的物体，把地面剔除，不检测自身
              if (!this.ray) this.ray = new Raycaster();

              const src = new Vector3(
                obj.position.x,
                obj.position.y + this.transformcontrol.objSize.y,
                obj.position.z
              );
              this.ray.set(src, new Vector3(0, -1, 0));
              const intersectObject = this.ray.intersectObjects(
                this.realFloor,
                false
              );
              if (intersectObject && intersectObject.length > 0) {
                this.transformcontrol.floorHeight = intersectObject[0].point.y;
              }
            }
          }
        }

        // 将修改数值更新到前端
        this.manager.interface.updateUIByChangeModel();
      }
    });
    if (this.transformScene) this.transformScene.add(this.transformcontrol);
    // 高版本不需要添加到场景中
    else this.scene.add(this.transformcontrol);
    this.transformcontrol.setSpace("global");
    this.transformcontrol.checkFloor = false;
  }

  _onKeyUp = (arg) => {
    if (this.webEditState) return; // 前端正在输入文字
    let event = Array.isArray(arg) ? arg[0] : arg;

    switch (event.code) {
      case "KeyM": {
        this.transformcontrol.setMode("translate");
        this.transformcontrol.setSpace("global");

        break;
      }
      case "KeyR": {
        this.transformcontrol.setSpace("global");
        this.transformcontrol.setMode("rotate");

        break;
      }
      case "KeyL": {
        this.transformcontrol.setSpace("local");
        this.transformcontrol.setMode("scale");

        break;
      }
      // F键 聚焦选中的物体
      case "KeyF": {
        if (this.currentSelectObj && this.currentSelectObj.length > 0) {
          this._focusObj(this.currentSelectObj);
        }
        break;
      }
      case "ShiftLeft":
      case "ShiftRight":
        this.shiftKey = false;
        this.transformcontrol.checkFloor = false;
        break;
      default:
        break;
    }
  };

  _onKeydown = (arg) => {
    if (this.webEditState) return; // 前端正在输入文字
    let event = Array.isArray(arg) ? arg[0] : arg;

    switch (event.code) {
      case "ShiftLeft":
      case "ShiftRight":
        this.shiftKey = true;
        this.transformcontrol.checkFloor = true;
        break;
    }
  };

  _onFocusin = (event) => {
    if (event.target.tagName === "INPUT") {
      //console.log("Focus is inside an input element");
      this.setEditState(true);
    }
  };

  _onFocusout = (event) => {
    if (event.target.tagName === "INPUT") {
      //console.log("Focus is outside an input element");
      this.setEditState(false);
    }
  };

  _addListener() {
    this.onKeyUpFunc = this._onKeyUp.bind(this);
    this.onKeyDownFunc = this._onKeydown.bind(this);
    this.resizeFunc = this._resize.bind(this);
    this.onFocusinFunc = this._onFocusin.bind(this);
    this.onFocusoutFunc = this._onFocusout.bind(this);

    this.manager.eventManager.addEventListener(
      EventManagerCommand.keyUp,
      this.onKeyUpFunc
    );
    this.manager.eventManager.addEventListener(
      EventManagerCommand.keyDown,
      this.onKeyDownFunc
    );
    this.manager.eventManager.addEventListener(
      EventManagerCommand.resize,
      this.resizeFunc
    );

    // 监听当前焦点是否在input标签上，避免键盘输入与前端冲突
    document.addEventListener("focusin", this.onFocusinFunc);
    document.addEventListener("focusout", this.onFocusoutFunc);
  }

  _removeListener() {
    this.manager.eventManager.removeEventListener(
      EventManagerCommand.keyUp,
      this.onKeyUpFunc
    );
    this.manager.eventManager.removeEventListener(
      EventManagerCommand.keyDown,
      this.onKeyDownFunc
    );
    this.manager.eventManager.removeEventListener(
      EventManagerCommand.resize,
      this.resizeFunc
    );

    // 监听当前焦点是否在input标签上，避免键盘输入与前端冲突
    document.removeEventListener("focusin", this.onFocusinFunc);
    document.removeEventListener("focusout", this.onFocusoutFunc);

    this.onKeyUpFunc = null;
    this.onKeyDownFunc = null;
    this.resizeFunc = null;
    this.onFocusinFunc = null;
    this.onFocusoutFunc = null;
  }


  _animate(dt) {
    if (!this.isRunning) return;

    if (this.controls) {
      this.controls.update();
    }

    if (this.customControls) this.customControls.update(dt);

    if (this.tween) {
      TWEEN.update();
    }

    if (this.outlineControl) this.outlineControl.update(dt);

    if (this.deltaTime)
      this.deltaTime.value = this.manager.interface.clock.getElapsedTime();

    

    this.stats.update();
    this.stats2.update();
    this.stats3.update();
    this._render();

    // Reset render context flag
    this.isRenderContextReShown = false;
  }

  _render() {
    this.renderer.info.reset();
    this.renderer.clear(true, true, true); // 清除颜色、深度和模板缓冲区
    //this.renderer.clearDepth();
    this.renderer.render(this.scene, this.activeCamera);

    if (this.transformScene){
      if(this.water){
        // 让水面波纹动起来，内部水面材质会根据 time 进行偏移
        this.water.material.uniforms['time'].value += 1.0/120.0;
      }
      this.renderer.render(this.transformScene, this.activeCamera);
    }
    //this.renderer.info.reset();
  }

  _resize() {
    if (!this.el || !this.el.parentElement) return;
    let { clientHeight, clientWidth } = this.el;

    if (this.state.aspect) clientHeight = clientWidth * this.state.aspect;

    this.defaultCamera.aspect = clientWidth / clientHeight;
    this.defaultCamera.updateProjectionMatrix();
    if (this.vignette)
      this.vignette.style({ aspect: this.defaultCamera.aspect });

    this.renderer.setSize(clientWidth, clientHeight);
    if (this.customControls) this.customControls.resize();
    if (this.outlineControl) this.outlineControl.resize();
  }

  loadLibModel(url, modelId, modelName, loadCompletedCB) {
    return new Promise(async (resolve, reject) => {
      const self = this;
      await this.manager.loadManager.loadGltfs(
        url,
        (gltf) => {
          let scene = gltf.scene || gltf.scenes[0];

          // 模型如果太大，先进行缩放 限制为100
          {
            let box = new Box3().expandByObject(scene);
            let boxSize = box.getSize(new Vector3());
            console.log(box,boxSize);
            if (Math.max(boxSize.x, boxSize.y, boxSize.z) > 0) {
              //this._uniformScene(scene, 15);
              this._uniformScene(scene,0);
              let newParent = new Object3D();
              newParent.position.copy(scene.position);
              newParent.scale.copy(scene.scale);
              newParent.rotation.copy(scene.rotation);
              newParent.add(scene);
              newParent.name = scene.name;
              // 原始模型后缀 skp需要缩小1000倍
              // if (
              //   gltf.asset.extras &&
              //   gltf.asset.extras.Sufix &&
              //   gltf.asset.extras.Sufix.toLowerCase() === ".skp"
              // ) {
              //   scene.scale.multiplyScalar(1 / 1000);
              // } else {
              //scene.scale.multiplyScalar(1 / 30);
              // }
              scene = newParent;
            }
          }

          if (modelName) scene.name = modelName;
          this._setupModel(scene, gltf, modelId);
          this.scene.add(scene);

          // 计算模型底面中心与模型位置的偏移
          const box = new Box3();
          box.expandByObject(scene);
          const center = box.getCenter(new Vector3());
          const size = box.getSize(new Vector3());
          scene._offset = scene.position
            .clone()
            .sub(new Vector3(center.x, center.y - size.y / 2, center.z));

          scene._modelUrl = url;

          if (loadCompletedCB) loadCompletedCB(scene);

          resolve(gltf);
        },
        null,
        (xhr) => {
          if (self.manager.isInVR()) {
            self.manager.vrManager.setProgress(xhr);
          } else if (self.manager.interface.loadModellibProgressCB) {
            self.manager.interface.loadModellibProgressCB(xhr);
          }
          //self.updateProgress(xhr);
        }
      );
    });
  }

  load(option, modelIndex, loadCompletedCB) {
    const url = option.modelurl;

    // console.log(option);
    //this.totalModelCount = urls.length;
    return new Promise(async (resolve, reject) => {
      const self = this;
      let hasSuccess=false

      await this.manager.loadManager.loadGltfs(
        url,
        (gltf) => {
          hasSuccess=true;
          let scene = gltf.scene || gltf.scenes[0];

          if (option.name) scene.name = option.name;

          let setState = (model, state) => {
            if (!model || !state) return;
            if (state.position) {
              model.position.set(
                state.position.x,
                state.position.y,
                state.position.z
              );
            }
            if (state.scale) {
              model.scale.set(state.scale.x, state.scale.y, state.scale.z);
            }
            if (state.rotation) {
              model.rotation.set(
                state.rotation._x,
                state.rotation._y,
                state.rotation._z
              );
            }
            if (state.visible !== undefined) {
              // 被删除的节点不添加到单体数组中
              if (state.visible === "delete") {
                model._delete = true;
                model.scale.set(0, 0, 0);
                model.visible = false;
              } else {
                model.visible = state.visible === "true" ? true : false;
                this.singleModels.push(model);
              }
            } else {
              this.singleModels.push(model);
            }
          };

          this.loadedModelCount++;

          // 从模型库加载的模型
          if (option.modelFrom) {
            // 模型如果太大，先进行缩放 限制为100
            {
              let box = new Box3().expandByObject(scene);
              let boxSize = box.getSize(new Vector3());
              if (Math.max(boxSize.x, boxSize.y, boxSize.z) > 0) {
                this._uniformScene(scene, 0);
                let newParent = new Object3D();
                newParent.position.copy(scene.position);
                newParent.scale.copy(scene.scale);
                newParent.rotation.copy(scene.rotation);
                newParent.add(scene);
                newParent.name = scene.name;
                // 原始模型后缀 skp需要缩小1000倍
                // if (
                //   gltf.asset.extras &&
                //   gltf.asset.extras.Sufix &&
                //   gltf.asset.extras.Sufix.toLowerCase() === ".skp"
                // ) {
                //   scene.scale.multiplyScalar(1 / 1000);
                // } else {
                //scene.scale.multiplyScalar(1 / 10);
                // }
                scene = newParent;
              }
            }

            this.scene.add(scene);
            scene.userData.modelFrom = option.modelFrom;
            scene._modelUrl = option.modelurl;
            const modelId = option.id;
            self._setupModel(scene, gltf, modelId);
            // 还原显隐和位置
            setState(scene, option.state);
            if (this.loadedModelCount === this.totalModelCount)
              this._loadCompleted(loadCompletedCB);
          }
          // 从图纸生成的模型
          else {
            this.paperPlane = null; // 图纸生成的模型固定有个“地形”对象作为地面

            let heightOffset = 0;
            // 未保存的图纸模型需要设置高度,最低点为0
            if (!option.singleStates) {
              let box = new Box3().expandByObject(scene);
              let boxSize = box.getSize(new Vector3());
              let center = box.getCenter(new Vector3());
              heightOffset = center.y - boxSize.y / 2 - 0.0001; // 增加0.0001的偏移是为了避免模型与地面重合闪烁
              //this.scene.add(new Box3Helper(box));
            }

            let count = 0;
            while (scene.children.length > 0) {
              const child = scene.children[0];
              if (child.name.indexOf("地形")>0) {
                child.traverse((node) => {
                  if (node.isMesh) {
                    this.paperPlane = node;
                  }
                });
              }

              // 对于图纸生成的模型，每个构件的材质都要复制出来，避免材质重用
              child.traverse((node) => {
                if (node.material) {
                  //node.material = node.material.clone();
                  console.log("跳过材质复制");
                }
              });

              child._modelUrl = option.modelurl;
              child._isPeperSingle = true;
              this.scene.add(child);

              this.scene.traverse(child=>{
                if (child.name.indexOf("地形")>0) {
                  this.floor.visible=false;
                }
                
              });

              // 保存过的模型
              if (option.singleStates) {
                let hasFind = false;
                for (var j = 0; j < option.singleStates.length; j++) {
                  if (child.name === option.singleStates[j].name) {
                    hasFind = true;
                    self._setupModel(child, gltf, option.singleStates[j].id);
                    setState(child, option.singleStates[j].state);
                    if (
                      0 === scene.children.length &&
                      this.loadedModelCount === this.totalModelCount
                    )
                      this._loadCompleted(loadCompletedCB);
                  }
                }
                if (!hasFind) {
                  // console.log(child);
                  child.scale.set(0, 0, 0);
                  child.visible = false;
                  child._delete = true; // 标记为删除，后续删除时会被清除掉
                  if (
                    0 === scene.children.length &&
                    this.loadedModelCount === this.totalModelCount
                  ) {
                    this._loadCompleted(loadCompletedCB);
                  }
                }

              }
              // 第一次加载的模型
              else {
                child.position.y -= heightOffset;
                this.singleModels.push(child);
                let modelId = option.id + "_" + count;
                if (!option.id) modelId = scene.uuid + "_" + count;
                self._setupModel(
                  child,
                  gltf,
                  modelId,
                  0 === scene.children.length &&
                    this.loadedModelCount === this.totalModelCount
                    ? loadCompletedCB
                    : null
                );
              }

              count++;
            }
          }

          resolve(gltf);
        },
        (gltfs) => {
          if(!hasSuccess){
            console.warn("gltf加载失败 跳过模型",gltfs);
            this.loadedModelCount++
            resolve(gltfs)
          }
        },
        (xhr) => {
          let process =
            xhr * self.state.loadratio + self.state.loadratioStart * 100;

          process = (
            (100 / self.totalModelCount) * modelIndex +
            process / self.totalModelCount
          ).toFixed(1);

          self.updateProgress(process);
        }
      );
    });
  }

  async _setupModel(scene, gltf, modelId, loadCompletedCB) {
    //遍历节点进行数据设置
    let traverseNodes = (scene, gltf_json_materials) => {
      scene.traverse((node) => {
        node._root = scene;

        if (node instanceof Mesh) {
          if (this.manager.interface.useShadow) {
            node.castShadow = true; // 物体投射阴影
            node.receiveShadow = true; // 地面接收阴影
          }

          node.material.depthWrite = true;
          node.material.transparent = false;
          node.material.vertexColors = false;
          node.material.side = DoubleSide;
          if (node.material.name !== null && gltf_json_materials) {
            for (let i = 0; i < gltf_json_materials.length; i++) {
              if (gltf_json_materials[i].name === node.material.name) {
                if (
                  gltf_json_materials[i].alphaMode !== undefined &&
                  gltf_json_materials[i].alphaMode === "BLEND"
                ) {
                  node.material.transparent = true;
                  if (
                    gltf_json_materials[i].pbrMetallicRoughness !== undefined &&
                    gltf_json_materials[i].pbrMetallicRoughness
                      .baseColorFactor &&
                    gltf_json_materials[i].pbrMetallicRoughness
                      .baseColorFactor[3] < 1
                  ) {
                    node.material.depthWrite = false;
                  } else node.material.depthWrite = true;
                } else node.material.transparent = false;

                if (gltf_json_materials[i].doubleSided) {
                  node.material.side = DoubleSide;
                }
                break;
              }
            }
          }

          if (!node.geometry.attributes.normal) {
            node.geometry.computeVertexNormals();
          }
        }
      });
    };

    // 增加node id
    function traverseChildrenForNodeId(obj, modelId) {
      if (obj.children && obj.children.length > 0) {
        for (var i = 0; i < obj.children.length; i++) {
          let name = obj.children[i].name;
          if (name.indexOf("@") >= 0) {
            let str = name.split("@");
            obj.children[i].nodeId = modelId + "|" + str[str.length - 1];
            obj.children[i].name = name.substring(0, name.lastIndexOf("@"));
          } else {
            obj.children[i].nodeId = obj.nodeId + "_" + i;
          }

          obj.children[i].modelId = modelId;

          if (obj.children[i].material)
            obj.children[i].material.modelId = modelId;

          traverseChildrenForNodeId(obj.children[i], modelId);
        }
      }
    }

    /**
     * 遍历所有节点为材质添加唯一id，以nodeId为准，如果有多个mesh共用材质，则合并使用nodeId
     * @param {*} scene
     */
    function setMaterialId(scene) {
      if (scene) {
        scene.traverse((node) => {
          if (node.material) {
            if (node.nodeId === undefined) {
              console.error("节点没有nodeId", node);
            } else {
              node.material.modelId = node.modelId;
              if (node.material.matId === undefined)
                node.material.matId = node.nodeId;
              else {
                // 共用材质拼接id
                //node.material.matId = node.material.matId + "_" + node.nodeId;
              }
            }
          }
        });
      }
    }

    scene = this.manager.tracker(scene);

    scene.modelId = modelId;
    if (scene.name.indexOf("@") >= 0) {
      let str = scene.name.split("@");
      scene.nodeId = modelId + "|" + str[str.length - 1];
      scene.name = scene.name.substring(0, scene.name.lastIndexOf("@"));
    } else {
      scene.nodeId = modelId + "|" + "-1";
    }
    traverseChildrenForNodeId(scene, modelId);
    setMaterialId(scene);

    //this._setContent(scene);

    this.state.addLights = true;

    traverseNodes(scene, gltf.parser.json.materials);

    if (!this.isInit) {
      if (loadCompletedCB) {
        this.isInit = true;
        this.loadedModelCount = 0;
        this._loadCompleted(loadCompletedCB);
      }
    }
  }

  _uniformScene(sceneModel, _uniforSize = 1) {
    var objBox3 = new Box3().setFromObject(sceneModel);
    var centerOffset = new Vector3();
    centerOffset.x = -(objBox3.min.x + objBox3.max.x) * 0.5;
    centerOffset.y = -(objBox3.min.y + objBox3.max.y) * 0.5;
    centerOffset.z = -(objBox3.min.z + objBox3.max.z) * 0.5;

    var size = objBox3.getSize(new Vector3());
    var maxSize = Math.max(size.x, size.y, size.z);

    var scale = !_uniforSize ? 1 : _uniforSize / maxSize; //如果传进来的_uniforSize === 0表示保持原比例缩放，只进行居中

    sceneModel.scale.x = scale;
    sceneModel.scale.y = scale;
    sceneModel.scale.z = scale;

    sceneModel.translateX(centerOffset.x * scale);
    sceneModel.translateY(centerOffset.y * scale);
    sceneModel.translateZ(centerOffset.z * scale);
  }

  _focusObj(obj) {
    if (obj) {
      if (!Array.isArray(obj)) obj = [obj];
      let box = new Box3();
      for (var i = 0; i < obj.length; i++) {
        box.expandByObject(obj[i]);
      }

      let center = box.getCenter(new Vector3());

      //center.y = 0;

      let size = box.getSize(new Vector3());
      let length = Math.max(size.x, size.y, size.z) * 1.0;

      let dir = this.activeCamera.position.clone().sub(center).normalize();
      let pos = center.clone().add(dir.multiplyScalar(length));

      // 获取相机的朝向向量
      const direction = new Vector3();
      this.activeCamera.getWorldDirection(direction);
      // 计算目标点：相机位置 + 方向向量 * 距离
      const distance = 10; // 假设目标点距离相机 10 个单位
      const targetPoint = new Vector3()
        .copy(this.activeCamera.position)
        .add(direction.multiplyScalar(distance));

      this._animateCamera(
        {
          x: this.activeCamera.position.x,
          y: this.activeCamera.position.y,
          z: this.activeCamera.position.z,
        },
        pos,
        this.controls
          ? {
              x: this.controls.target.x,
              y: this.controls.target.y,
              z: this.controls.target.z,
            }
          : targetPoint,
        center,
        null,
        1000
      );

      // this.activeCamera.position.copy(pos);
      // if (this.controls) this.controls.target.copy(center);

      // this.activeCamera.lookAt(center);
    }
  }
  /**
   * 视角居中
   */
  centerScene = () => {
    let box = new Box3();
    for (var i = 0; i < this.singleModels.length; i++) {
      if (this.singleModels[i]._delete) continue;
      box.expandByObject(this.singleModels[i]);
      //this.singleModels[i].updateMatrixWorld();
      //box.union(this.getBoxFromObj(this.singleModels[i]));
    }

    let center = box.getCenter(new Vector3());

    center.y = 0;

    let size = box.getSize(new Vector3());
    let length = Math.max(size.x, size.y, size.z);

    // if (length * 5 < 1000) {
    //   this.activeCamera.far = 1000;
    // } else {
    //   this.activeCamera.far = length * 5;
    // }
    //this.activeCamera.near = length / 1000;
    //this.activeCamera.updateProjectionMatrix();

    let pos = new Vector3(
      center.x + length,
      center.y + length / 5,
      center.z + length
    );
    if (this.controls) {
      this.activeCamera._position = center; //pos
      this._animateCamera(
        {
          x: this.activeCamera.position.x,
          y: this.activeCamera.position.y,
          z: this.activeCamera.position.z,
        },
        center,
        {
          x: this.controls.target.x,
          y: this.controls.target.y,
          z: this.controls.target.z,
        },
        center,
        null,
        100
      );
    } else if (this.customControls) {
      this.activeCamera.position.copy(pos);
    }
  };

  /**
   * 所有模型加载完成
   */
  async _loadCompleted(cb) {
    //this.centerScene();

    /*this.scene.traverse(child=>{
      if (child.name.indexOf("地形")!=0) {
        this.floor.visible=false;
        console.log("隐藏了地板");
      }
    });*/
    console.log(this.scene);

    //找到场景中的太阳光
    this.scene.traverse((child) => {
      if (child.type == "DirectionalLight") {
        this.sunLight = child;
      }
      else if(child.name.indexOf("地形")>0){
        child.traverse((node)=>{
          if(node.geometry){
            const geometry=node.geometry;
            geometry.computeBoundingBox();
            const bbox = geometry.boundingBox;
            const sizeX = bbox.max.x - bbox.min.x;
            const sizeZ = bbox.max.z - bbox.min.z;

            const uv = [];
            const pos = geometry.attributes.position;
            const repeat = 10; // 纹理重复次数，可调整

            for (let i = 0; i < pos.count; i++) {
              const x = pos.getX(i);
              const z = pos.getZ(i);
              const u = (x - bbox.min.x) / sizeX * repeat;
              const v = (z - bbox.min.z) / sizeZ * repeat;
              uv.push(u, v);
            }

            geometry.setAttribute('uv', new Float32BufferAttribute(uv, 2));

            node.material=terrainMaterial;
            console.log(node.material);
            
          }
        });
      }
      else if(child.name.indexOf("平台轮廓")>0){
        child.traverse((node)=>{
          if(node.geometry){
            const geometry=node.geometry;
            geometry.computeBoundingBox();
            const bbox = geometry.boundingBox;
            const sizeX = bbox.max.x - bbox.min.x;
            const sizeZ = bbox.max.z - bbox.min.z;

            const uv = [];
            const pos = geometry.attributes.position;
            const repeat = 10; // 纹理重复次数，可调整

            for (let i = 0; i < pos.count; i++) {
              const x = pos.getX(i);
              const z = pos.getZ(i);
              const u = (x - bbox.min.x) / sizeX * repeat;
              const v = (z - bbox.min.z) / sizeZ * repeat;
              uv.push(u, v);
            }

            geometry.setAttribute('uv', new Float32BufferAttribute(uv, 2));

            node.material=platMaterial;
            node.renderOrder=2;
          }
        });
      }
      else if(child.name.indexOf("水面")>0){
        child.traverse((node)=>{
          if(node.geometry){
            const waterGeometry=node.geometry;

            const waterPos=node.position.clone();
            const waterRot=node.rotation.clone();
            const waterScale=node.scale.clone();

            const textureLoader = new TextureLoader();
            const waterNormals = textureLoader.load("/assets/waternormals.jpg", function (texture) {
              texture.wrapS = texture.wrapT = RepeatWrapping; // 让法线贴图可以重复
            });
            const water = new Water(waterGeometry, {
              textureWidth: 512, // 生成的反射/折射纹理宽度
              textureHeight: 512, // 生成的反射/折射纹理高度
              waterNormals: waterNormals, // 法线贴图
              alpha: 1, // 水面的透明度
              //sunDirection: new Vector3(0, 0, 1),// 模拟光线方向
              sunColor: 0xD4F2E7, // “太阳光”颜色
              waterColor: 0x294f9a, // 水体颜色
              distortionScale: 20 // 失真程度，决定波纹起伏的大小
            });

            water.position.copy(waterPos);
            water.rotation.copy(waterRot);
            water.scale.copy(waterScale);

            node.parent.add(water);
            node.parent.remove(node);

            this.water=water;
            node.renderOrder=3;
          }
        });
      }
    });

    if (cb) cb();


    //if (this.controls) this.centerScene();
  }

  _addLights() {
    this.ambientLight = new AmbientLight(
      this.state.ambientColor,
      this.state.ambientIntensity
    );
    this.scene.add(this.ambientLight);
  }

  /**
   * 设置环境光强度
   * @param {*} v
   */
  setAmbientIntensity(v) {
    if (this.ambientLight) this.ambientLight.intensity = v;
  }

  /**
   * 获取环境光强度
   * @returns
   */
  getAmbientIntensity() {
    if (this.ambientLight) return this.ambientLight.intensity;
    else return 0;
  }

  /**
   * 设置环境光颜色
   * @param {*} c
   */
  setAmbientColor(c) {
    if (this.ambientLight) this.ambientLight.color = new Color(c);
  }

  /**
   * 获取环境光颜色
   * @returns
   */
  getAmbientColor() {
    if (this.ambientLight) {
      let color = this.ambientLight.color.getHex().toString(16);
      if (color === "0") color = "000000";
      color = "#" + color;
      return color;
    }
    return "#ffffff";
  }

  _createPlane() {
    let geo = new PlaneGeometry(1000, 1000);
    new TextureLoader().load("/assets/imgs/grass.jpg", (tex) => {
      if (tex.encoding) tex.colorSpace = SRGBColorSpace;
      if (tex.colorSpace) tex.colorSpace = SRGBColorSpace;
      tex.wrapS = RepeatWrapping;
      tex.wrapT = RepeatWrapping;
      tex.repeat = new Vector2(500, 500);
      let mat = new MeshStandardMaterial({ map: tex, side: DoubleSide });
      this.floor = new Mesh(geo, mat);
      this.floor.receiveShadow = true;
      this.scene.add(this.floor);

      this.floor.rotateX(-Math.PI / 2);
    });
  }

  attach(obj) {
    this.detach();
    if (!obj) return;
    if (!Array.isArray(obj)) obj = [obj];

    // 单个物体
    if (obj.length === 1) {
      obj = obj[0];
      if (!obj.parent) return;
      let box = new Box3().expandByObject(obj);
      let center = box.getCenter(new Vector3());

      let parent = new Object3D();
      parent._type = "uniformParent";
      parent.position.copy(center);
      parent.rotation.copy(obj.rotation);
      parent.scale.copy(obj.scale);
      parent.name = obj.name;
      obj.parent.attach(parent);
      obj.userData._attachParent = obj.parent;
      parent.attach(obj);
      this.currentSelectParent = parent;

      // VR模式不需要TransformControl
      if (!this.manager.isInVR()) this.transformcontrol.attach(parent);

      this.setOutlineObjects(parent);
    }
    // 多个物体
    else if (obj.length > 1) {
      let box = new Box3();
      for (var i = 0; i < obj.length; i++) {
        if (!obj[i].parent) continue;
        box.expandByObject(obj[i]);
        obj[i].userData._attachParent = obj[i].parent;
      }
      let center = box.getCenter(new Vector3());

      let parent = new Object3D();
      parent._type = "uniformParent";
      parent.position.copy(center);
      this.scene.add(parent);
      for (var j = 0; j < obj.length; j++) {
        parent.attach(obj[j]);
      }
      this.currentSelectParent = parent;
      // VR模式不需要TransformControl
      if (!this.manager.isInVR()) this.transformcontrol.attach(parent);

      this.setOutlineObjects(parent);
    }

    this.currentSelectObj = Array.isArray(obj) ? obj : [obj];

    // 收集地面用于地面吸附检测
    this.realFloor = [this.floor];
    if (this.manager.vrSettingManager.planeNodes) {
      for (
        var m = 0;
        m < this.manager.vrSettingManager.planeNodes.length;
        m++
      ) {
        if (
          this.manager.vrSettingManager.planeNodes[m]._root &&
          this.manager.vrSettingManager.planeNodes[m]._root !== obj
        ) {
          this.realFloor.push(this.manager.vrSettingManager.planeNodes[m]);
        }
      }
    }
    //}
  }

  detach() {
    this.currentSelectObj = [];

    if (!this.currentSelectParent) return;

    // VR模式不需要TransformControl
    if (!this.manager.isInVR()) this.transformcontrol.detach();

    this.setOutlineObjects(null);

    if (
      this.currentSelectParent._type &&
      this.currentSelectParent._type === "uniformParent"
    ) {
      while (this.currentSelectParent.children.length > 0) {
        const c = this.currentSelectParent.children[0];
        if (c.userData._attachParent) {
          c.userData._attachParent.attach(c);
          delete c.userData._attachParent;
          continue;
        }
        c.removeFromParent();
      }
      this.currentSelectParent.removeFromParent();
    }
    this.currentSelectParent = null;
  }

  setOutlineObjects(objs) {
    if (this.outlineControl) {
      this.outlineControl.setOutlineObjects(objs);
    }
  }

  setVRLoop(callback) {
    if (this.renderer && callback) {
      this.renderer.setAnimationLoop(callback);
    }
  }

  // ============================================================迁移功能
  setUpdateProgress(cb) {
    if (cb) this.updateProgressCb = cb;
  }

  updateProgress(value) {
    if (value >= 99.8) value = 99.8;
    if (this.updateProgressCb) this.updateProgressCb(value);
  }

  /**
   *
   * @param {*} oldP 相机原来的位置
   * @param {*} newP 相机新的位置
   */
  _animateCamera(current1, current2, target1, target2, cb, timer) {
    let time = timer ? timer : 1500;

    let positionVar = {
      x1: current1.x,
      y1: current1.y,
      z1: current1.z,
      x2: target1.x,
      y2: target1.y,
      z2: target1.z,
    };

    var tween = new TWEEN.Tween(positionVar);
    tween.to(
      {
        x1: current2.x,
        y1: current2.y,
        z1: current2.z,
        x2: target2.x,
        y2: target2.y,
        z2: target2.z,
      },
      time
    );
    tween.onUpdate((object) => {
      this.activeCamera.position.x = object.x1;
      this.activeCamera.position.y = object.y1;
      this.activeCamera.position.z = object.z1;
      if (this.controls) {
        this.controls.target.x = object.x2;
        this.controls.target.y = object.y2;
        this.controls.target.z = object.z2;
        this.controls.update();
      } else {
        this.activeCamera.lookAt(new Vector3(object.x2, object.y2, object.z2));
      }
    });
    tween.onComplete(() => {
      this.tween = null;
      if (cb) cb();
    });
    tween.easing(TWEEN.Easing.Cubic.InOut);
    tween.start();
    this.tween = tween;
  }

  _setContent = async (object) => {
    let box = new Box3().setFromObject(object); // 获取模型的包围盒

    let boxSize = box.getSize(new Vector3());

    let boxCenter = box.getCenter(new Vector3());
    const targetPos = new Vector3(
      boxCenter.x,
      boxCenter.y - boxSize.y / 2,
      boxCenter.z
    );
    const offset = new Vector3(0, 0, 0).sub(targetPos);
    object.position.add(offset);

    this.scene.add(object);
    this.singleModels.push(object);
  };

  /**
   * 平滑的移动相机到目标位置
   * @param {*} pos
   */
  lerpCamera(pos) {
    // 获取相机的朝向向量
    const direction = new Vector3();
    this.activeCamera.getWorldDirection(direction);
    // 计算目标点：相机位置 + 方向向量 * 距离
    const distance = 10; // 假设目标点距离相机 10 个单位
    const srcTargetPoint = new Vector3()
      .copy(this.activeCamera.position)
      .add(direction.multiplyScalar(distance));

    const dir = pos.clone().sub(this.activeCamera.position).normalize();
    const targetPoint = new Vector3()
      .copy(pos)
      .add(dir.multiplyScalar(distance));

    this._animateCamera(
      {
        x: this.activeCamera.position.x,
        y: this.activeCamera.position.y,
        z: this.activeCamera.position.z,
      },
      pos,
      this.controls
        ? {
            x: this.controls.target.x,
            y: this.controls.target.y,
            z: this.controls.target.z,
          }
        : srcTargetPoint,
      targetPoint,
      null,
      1000
    );
  }

  /**
   * 设置前端是否是在文字输入（修改材质名称，搜索等），避免与3D控制器冲突
   * @param {*} state
   */
  setEditState(state) {
    this.webEditState = state;
    if (this.webEditState) {
      if (this.customControls) this.customControls.enabled = false;
    } else {
      if (this.customControls) this.customControls.enabled = true;
    }
  }

  getView() {
    return this.viewMode;
  }

  /**
   * 切换透视图、顶视图、前视图、后视图、左视图、右视图
   * @param {*} view "default"/"top"/"front"/"back"/"left"/"right"
   */
  setupView(view) {
    const box = new Box3();
    for (var i = 0; i < this.singleModels.length; i++) {
      box.expandByObject(this.singleModels[i]);
    }
    //box.expandByObject(this.floor);
    const size = new Vector3();
    box.getSize(size); // 获取包围盒的尺寸
    const maxSize = Math.max(size.x, size.y, size.z);
    const center = new Vector3();
    box.getCenter(center); // 获取包围盒的中心

    // 增加场景尺寸有效性校验
    if (maxSize === 0) {
      console.warn("场景中没有可见对象");
      return;
    }

    this.viewMode = view;

    // 调整正交相机参数
    const frustumSize = maxSize; // 扩大可视范围
    const aspect =
      this.renderer.domElement.width / this.renderer.domElement.height;

    if (view !== "default") {
      this.orthographicCamera = new OrthographicCamera(
        (frustumSize * aspect) / -2, // left
        (frustumSize * aspect) / 2, // right
        frustumSize / 2, // top
        frustumSize / -2, // bottom
        1, // near (原0.1)
        maxSize * 10 // far (原1000)
      );

      // 设置相机位置后必须更新投影矩阵
      //this.orthographicCamera.updateProjectionMatrix();
      this.activeCamera = this.orthographicCamera;

      // 根据视图类型设置相机的位置和朝向
      switch (view) {
        case "front":
          {
            this.orthographicCamera.position.set(
              center.x,
              center.y,
              center.z + maxSize
            );
          }
          // if (this.manager.clippingManager)
          //   this.manager.clippingManager.changeClipAxis(0, "+Z");
          break;
        case "back":
          {
            this.orthographicCamera.position.set(
              center.x,
              center.y,
              center.z - maxSize
            );
          }
          // if (this.manager.clippingManager)
          //   this.manager.clippingManager.changeClipAxis(0, "-Z");
          break;
        case "top":
          {
            this.orthographicCamera.position.set(
              center.x,
              center.y + maxSize,
              center.z
            );
            this.orthographicCamera.up.set(0, 0, 1); // 设置UP向量为Z轴
          }
          // if (this.manager.clippingManager)
          //   this.manager.clippingManager.changeClipAxis(0, "+Y");
          break;
        case "left":
          {
            this.orthographicCamera.position.set(
              center.x - maxSize,
              center.y,
              center.z
            );
          }
          // if (this.manager.clippingManager)
          //   this.manager.clippingManager.changeClipAxis(0, "-X");
          break;
        case "right":
          {
            this.orthographicCamera.position.set(
              center.x + maxSize,
              center.y,
              center.z
            );
          }
          // if (this.manager.clippingManager)
          //   this.manager.clippingManager.changeClipAxis(0, "+X");
          break;
        default: {
          break;
        }
      }
      this.orthographicCamera.lookAt(center);
      this.orthographicCamera.updateProjectionMatrix(); // 必须再次更新
    } else {
      this.orthographicCamera = null;
      this.activeCamera = this.defaultCamera;

      // if (this.manager.clippingManager) this.manager.clippingManager.exitClip();
    }

    if (this.customControls) {
      this.customControls.object = this.activeCamera;
    }
    if (this.transformcontrol) {
      this.transformcontrol.camera = this.activeCamera;
    }
    if (this.outlineControl) {
      this.outlineControl.setCamera(this.activeCamera);
    }

    // if (this.manager.clippingManager)
    //   this.manager.clippingManager.transformcontrol_box.camera =
    //     this.activeCamera;
  }

  /**
   * 添加雾效
   */
  _addFog() {
    this.scene.fog = new FogExp2(0xffffff);
  }

  /**
   * 设置雾效密度
   * @param {*} d
   */
  setFogDensity(d) {
    if (this.scene.fog) this.scene.fog.density = d;
  }

  /**
   * 获取雾效密度
   * @returns
   */
  getFogDensity() {
    if (this.scene.fog) return this.scene.fog.density;
    return 0;
  }

  /**
   * 设置雾效颜色
   * @param {*} c
   */
  setFogColor(c) {
    if (this.scene.fog) this.scene.fog.color = new Color(c);
  }

  /**
   * 获取雾效颜色
   * @returns
   */
  getFogColor() {
    if (this.scene.fog) {
      let color = this.scene.fog.color.getHex().toString(16);
      if (color === "0") color = "000000";
      color = "#" + color;
      return color;
    }
    return "#ffffff";
  }


}

function traverseMaterials(object, callback) {
  object.traverse((node) => {
    if (!node.isMesh) return;
    const materials = Array.isArray(node.material)
      ? node.material
      : [node.material];
    materials.forEach(callback);
  });
}
